import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ClipboardList, Copy, Check, Link2, QrCode,
  Download, Share2, Mail, MessageSquare, ChevronLeft,
  AlertCircleIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import type { ProgramRecord } from '@/types/programs'
import type { ProspectCreatePayload } from '@/types/prospects'

type Tab = 'link' | 'qr' | 'form'

interface AddProspectMethodDialogProps {
  open: boolean
  programId?: string
  agentCode: string
  programs?: ProgramRecord[]
  onClose: () => void
  /** Form tab props */
  isSubmittingForm?: boolean
  formError?: ApiError | null
  onFormSubmit?: (payload: ProspectCreatePayload) => Promise<void>
}

type FormState = {
  contact_name: string
  contact_email: string
  contact_phone_raw: string
  company_name: string
}

const emptyForm = (): FormState => ({
  contact_name: '',
  contact_email: '',
  contact_phone_raw: '',
  company_name: '',
})

export function AddProspectMethodDialog({
  open,
  programId: fixedProgramId,
  agentCode,
  programs = [],
  onClose,
  isSubmittingForm = false,
  formError = null,
  onFormSubmit,
}: AddProspectMethodDialogProps) {
  const { t } = useTranslation()
  const [inSelect, setInSelect] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>('link')
  const [copied, setCopied] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [sharingQr, setSharingQr] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [clientError, setClientError] = useState<string | null>(null)

  const eligiblePrograms = programs.filter((p) => p.status === 'active')
  const activeProgramId = fixedProgramId ?? selectedId
  const needsProgramSelect = !fixedProgramId && eligiblePrograms.length > 1

  const portalUrl = activeProgramId && agentCode
    ? `${window.location.origin}/portal/${encodeURIComponent(agentCode)}/${encodeURIComponent(activeProgramId)}`
    : ''
  const qrApiUrl = portalUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(portalUrl)}`
    : ''
  const activeShareMessage = portalUrl ? t('prospects.dialogs.addMethod.shareMessage', { url: portalUrl }) : ''

  useEffect(() => {
    if (!open) return
    setInSelect(true)
    setActiveTab('link')
    setCopied(false)
    setSelectedId(fixedProgramId ? '' : (eligiblePrograms[0]?.id ?? ''))
    setSharingQr(false)
    setForm(emptyForm())
    setClientError(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleClose() {
    onClose()
  }

  function enterTabs(tab: Tab) {
    if (!fixedProgramId && eligiblePrograms.length === 1) {
      setSelectedId(eligiblePrograms[0]!.id)
    }
    setActiveTab(tab)
    setInSelect(false)
  }

  async function handleCopy() {
    if (!portalUrl) return
    await navigator.clipboard.writeText(portalUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleDownloadQr() {
    if (!qrApiUrl) return
    const a = document.createElement('a')
    a.href = qrApiUrl
    a.download = `qr-${activeProgramId}.png`
    a.target = '_blank'
    a.click()
  }

  async function handleNativeShare() {
    if (!portalUrl) return
    if (!navigator.share) {
      await navigator.clipboard.writeText(activeShareMessage)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      return
    }
    try { await navigator.share({ text: activeShareMessage, url: portalUrl }) } catch { /* cancelled */ }
  }

  async function handleNativeShareQr() {
    if (!qrApiUrl || !portalUrl) return
    setSharingQr(true)
    try {
      const res = await fetch(qrApiUrl)
      const blob = await res.blob()
      const file = new File([blob], `qr-${activeProgramId}.png`, { type: 'image/png' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: activeShareMessage })
      } else if (navigator.share) {
        await navigator.share({ text: activeShareMessage, url: portalUrl })
      } else {
        await navigator.clipboard.writeText(activeShareMessage)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch { /* cancelled */ } finally { setSharingQr(false) }
  }

  function handleWhatsApp() {
    if (!portalUrl) return
    window.open(`https://wa.me/?text=${encodeURIComponent(activeShareMessage)}`, '_blank', 'noopener,noreferrer')
  }

  function handleEmail() {
    if (!portalUrl) return
    window.open(
      `mailto:?subject=${encodeURIComponent(t('prospects.dialogs.addMethod.emailSubject'))}&body=${encodeURIComponent(activeShareMessage)}`,
      '_self',
    )
  }

  function handleSms() {
    if (!portalUrl) return
    window.open(`sms:?body=${encodeURIComponent(activeShareMessage)}`, '_self')
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!onFormSubmit) return
    if (!form.contact_email.trim() && !form.contact_phone_raw.trim()) {
      setClientError(t('prospects.dialogs.addMethod.formTab.contactRequiredError'))
      return
    }
    setClientError(null)
    await onFormSubmit({
      program_id: activeProgramId,
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim() || null,
      contact_phone_raw: form.contact_phone_raw.trim() || null,
      company_name: form.company_name.trim() || null,
    })
  }

  const hasNativeShare = typeof navigator !== 'undefined' && 'share' in navigator

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="sm:max-w-md w-full p-0 shadow-none rounded-[0.625rem] overflow-hidden" showCloseButton>

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            {!inSelect && (
              <button
                type="button"
                onClick={() => setInSelect(true)}
                className="flex items-center justify-center size-7 rounded-[0.625rem] hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                aria-label={t('prospects.dialogs.addMethod.backAriaLabel')}
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-sm font-semibold leading-none">{t('prospects.dialogs.addMethod.title')}</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {inSelect ? t('prospects.dialogs.addMethod.selectDescription') : t(`prospects.dialogs.addMethod.tabs.${activeTab}`)}
              </p>
            </div>
          </div>

          {/* Tabs strip — only in content step */}
          {!inSelect && (
            <div className="mt-3.5">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
                <TabsList className="w-full">
                  <TabsTrigger value="link" className="flex-1 gap-1.5">
                    <Link2 className="size-3.5" />
                    {t('prospects.dialogs.addMethod.tabs.link')}
                  </TabsTrigger>
                  <TabsTrigger value="qr" className="flex-1 gap-1.5">
                    <QrCode className="size-3.5" />
                    {t('prospects.dialogs.addMethod.tabs.qr')}
                  </TabsTrigger>
                  <TabsTrigger value="form" className="flex-1 gap-1.5">
                    <ClipboardList className="size-3.5" />
                    {t('prospects.dialogs.addMethod.tabs.form')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
        </DialogHeader>

        {/* ── Body ── */}
        <div className="px-6 py-5">

          {/* ── SELECT STEP ── */}
          {inSelect && (
            <div className="space-y-2">
              <MethodCard
                icon={<Link2 className="size-4.5" />}
                iconBg="bg-primary/10 text-primary"
                title={t('prospects.dialogs.addMethod.methods.link.title')}
                description={t('prospects.dialogs.addMethod.methods.link.description')}
                fullWidth
                onClick={() => enterTabs('link')}
              />
              <div className="grid grid-cols-2 gap-2">
                <MethodCard
                  icon={<QrCode className="size-4.5" />}
                  iconBg="bg-primary/10 text-primary"
                  title={t('prospects.dialogs.addMethod.methods.qr.title')}
                  description={t('prospects.dialogs.addMethod.methods.qr.description')}
                  onClick={() => enterTabs('qr')}
                />
                <MethodCard
                  icon={<ClipboardList className="size-4.5" />}
                  iconBg="bg-emerald-500/10 text-emerald-500"
                  title={t('prospects.dialogs.addMethod.methods.form.title')}
                  description={t('prospects.dialogs.addMethod.methods.form.description')}
                  onClick={() => enterTabs('form')}
                />
              </div>
            </div>
          )}

          {/* ── CONTENT STEP ── */}
          {!inSelect && (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>

              {/* ── LINK TAB ── */}
              <TabsContent value="link" className="mt-0 space-y-4">
                {needsProgramSelect && (
                  <ProgramSelector programs={eligiblePrograms} value={selectedId} onChange={setSelectedId} />
                )}
                <div className="space-y-1.5">
                  <p className="text-sm text-muted-foreground">{t('prospects.dialogs.addMethod.linkTab.yourLink')}</p>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={portalUrl}
                      className="flex-1 text-xs font-mono min-w-0"
                      onFocus={(e) => e.target.select()}
                      placeholder={needsProgramSelect && !selectedId ? t('prospects.dialogs.addMethod.linkTab.selectProgramPlaceholder') : ''}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopy}
                      disabled={!portalUrl}
                      aria-label={t('prospects.dialogs.addMethod.linkTab.copyAriaLabel')}
                      className="shrink-0"
                    >
                      {copied ? <Check className="size-4 text-emerald-500" /> : <Copy className="size-4" />}
                    </Button>
                  </div>
                  {copied && <p className="text-xs text-emerald-500 font-medium">{t('prospects.dialogs.addMethod.linkTab.copied')}</p>}
                </div>
                <ShareButtons
                  disabled={!portalUrl}
                  hasNativeShare={hasNativeShare}
                  onNativeShare={handleNativeShare}
                  onWhatsApp={handleWhatsApp}
                  onEmail={handleEmail}
                  onSms={handleSms}
                />
              </TabsContent>

              {/* ── QR TAB ── */}
              <TabsContent value="qr" className="mt-0 space-y-4">
                {needsProgramSelect && (
                  <ProgramSelector programs={eligiblePrograms} value={selectedId} onChange={setSelectedId} />
                )}
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center justify-center rounded-[0.625rem] border border-border bg-white p-3 min-h-[200px] w-full">
                    {portalUrl ? (
                      <img
                        src={qrApiUrl}
                        alt={t('prospects.dialogs.addMethod.qrTab.qrAlt')}
                        width={200}
                        height={200}
                        className="block"
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground text-center px-4">
                        {t('prospects.dialogs.addMethod.qrTab.selectProgramPrompt')}
                      </p>
                    )}
                  </div>
                  {portalUrl && (
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={handleDownloadQr}>
                      <Download className="size-4 mr-1.5" />
                      {t('prospects.dialogs.addMethod.qrTab.download')}
                    </Button>
                  )}
                </div>
                <ShareButtons
                  disabled={!portalUrl}
                  hasNativeShare={hasNativeShare}
                  onNativeShare={handleNativeShareQr}
                  nativeShareLoading={sharingQr}
                  onWhatsApp={handleWhatsApp}
                  onEmail={handleEmail}
                  onSms={handleSms}
                />
              </TabsContent>

              {/* ── FORM TAB ── */}
              <TabsContent value="form" className="mt-0">
                <form id="method-prospect-form" className="space-y-4" onSubmit={handleFormSubmit}>
                  {needsProgramSelect && (
                    <ProgramSelector programs={eligiblePrograms} value={selectedId} onChange={setSelectedId} />
                  )}

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground" htmlFor="mp-name">{t('prospects.dialogs.addMethod.formTab.contactNameLabel')}</label>
                      <Input
                        id="mp-name"
                        value={form.contact_name}
                        onChange={(e) => setForm((s) => ({ ...s, contact_name: e.target.value }))}
                        placeholder={t('prospects.dialogs.addMethod.formTab.contactNamePlaceholder')}
                        required
                      />
                      {formError?.errors?.contact_name?.[0] && (
                        <p className="text-xs text-destructive">{formError.errors.contact_name[0]}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground" htmlFor="mp-company">{t('prospects.dialogs.addMethod.formTab.companyLabel')}</label>
                      <Input
                        id="mp-company"
                        value={form.company_name}
                        onChange={(e) => setForm((s) => ({ ...s, company_name: e.target.value }))}
                        placeholder={t('prospects.dialogs.addMethod.formTab.companyPlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground" htmlFor="mp-email">{t('prospects.dialogs.addMethod.formTab.emailLabel')}</label>
                      <Input
                        id="mp-email"
                        type="email"
                        value={form.contact_email}
                        onChange={(e) => setForm((s) => ({ ...s, contact_email: e.target.value }))}
                        placeholder={t('prospects.dialogs.addMethod.formTab.emailPlaceholder')}
                      />
                      {formError?.errors?.contact_email?.[0] && (
                        <p className="text-xs text-destructive">{formError.errors.contact_email[0]}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground" htmlFor="mp-phone">{t('prospects.dialogs.addMethod.formTab.phoneLabel')}</label>
                      <Input
                        id="mp-phone"
                        value={form.contact_phone_raw}
                        onChange={(e) => setForm((s) => ({ ...s, contact_phone_raw: e.target.value }))}
                        placeholder={t('prospects.dialogs.addMethod.formTab.phonePlaceholder')}
                      />
                      {formError?.errors?.contact_phone_raw?.[0] && (
                        <p className="text-xs text-destructive">{formError.errors.contact_phone_raw[0]}</p>
                      )}
                    </div>
                  </div>

                  {clientError && (
                    <Alert variant="destructive">
                      <AlertCircleIcon />
                      <AlertTitle>{t('prospects.dialogs.addMethod.formTab.contactRequiredAlertTitle')}</AlertTitle>
                      <AlertDescription>{clientError}</AlertDescription>
                    </Alert>
                  )}

                  {formError && !formError.errors && (
                    <Alert variant="destructive">
                      <AlertCircleIcon />
                      <AlertTitle>{t('prospects.dialogs.addMethod.formTab.createErrorAlertTitle')}</AlertTitle>
                      <AlertDescription>{formError.message}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={isSubmittingForm}>
                      {t('prospects.dialogs.addMethod.formTab.cancel')}
                    </Button>
                    <Button type="submit" size="sm" disabled={isSubmittingForm || !activeProgramId}>
                      {isSubmittingForm ? t('prospects.dialogs.addMethod.formTab.submitting') : t('prospects.dialogs.addMethod.formTab.submit')}
                    </Button>
                  </div>
                </form>
              </TabsContent>

            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Sub-components ─── */

function ProgramSelector({
  programs,
  value,
  onChange,
}: {
  programs: ProgramRecord[]
  value: string
  onChange: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5">
      <label className="text-sm text-muted-foreground">{t('prospects.dialogs.addMethod.programSelector.label')}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t('prospects.dialogs.addMethod.programSelector.placeholder')} />
        </SelectTrigger>
        <SelectContent position="popper" className="w-[var(--radix-popper-anchor-width)]">
          {programs.map((p) => (
            <SelectItem key={p.id} value={p.id} className="max-w-full">
              <span className="block truncate">{p.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ShareButtons({
  disabled,
  hasNativeShare,
  onNativeShare,
  nativeShareLoading = false,
  onWhatsApp,
  onEmail,
  onSms,
}: {
  disabled: boolean
  hasNativeShare: boolean
  onNativeShare: () => void
  nativeShareLoading?: boolean
  onWhatsApp: () => void
  onEmail: () => void
  onSms: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('prospects.dialogs.addMethod.share.via')}</p>
      <div className="grid grid-cols-2 gap-2">
        {hasNativeShare && (
          <Button
            type="button" variant="outline" size="sm"
            disabled={disabled || nativeShareLoading}
            onClick={onNativeShare}
            className="gap-2 col-span-2"
          >
            <Share2 className="size-4" />
            {nativeShareLoading ? t('prospects.dialogs.addMethod.share.nativeShareLoading') : t('prospects.dialogs.addMethod.share.nativeShare')}
          </Button>
        )}
        <Button
          type="button" variant="outline" size="sm"
          disabled={disabled}
          onClick={onWhatsApp}
          className="gap-2 border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/10 hover:border-[#25D366]/60"
        >
          <svg viewBox="0 0 24 24" className="size-4 fill-current shrink-0" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          {t('prospects.dialogs.addMethod.share.whatsapp')}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onEmail} className="gap-2">
          <Mail className="size-4 shrink-0" />
          {t('prospects.dialogs.addMethod.share.email')}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onSms} className="gap-2 col-span-2">
          <MessageSquare className="size-4 shrink-0" />
          {t('prospects.dialogs.addMethod.share.sms')}
        </Button>
      </div>
    </div>
  )
}

function MethodCard({
  icon, iconBg, title, description, fullWidth = false, onClick,
}: {
  icon: React.ReactNode
  iconBg: string
  title: string
  description: string
  fullWidth?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full rounded-[0.625rem] border border-border bg-background transition-colors select-none',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        fullWidth ? 'flex items-center gap-3.5 px-4 py-3.5' : 'flex flex-col gap-2.5 p-4',
      )}
    >
      <div className={cn('flex shrink-0 items-center justify-center rounded-[0.625rem] size-9', iconBg)}>
        {icon}
      </div>
      <div className="text-left min-w-0">
        <p className="text-sm font-medium text-foreground leading-snug">{title}</p>
        <p className="text-xs text-muted-foreground leading-snug mt-0.5">{description}</p>
      </div>
    </button>
  )
}
